import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Search, 
  Star, 
  Users, 
  Shield, 
  Zap, 
  Award,
  ArrowRight,
  CheckCircle,
  Globe,
  DollarSign
} from 'lucide-react';
import AuthModalWidget from '../components/AuthModalWidget';

const HomePage = () => {
  const { isAuthenticated, user } = useAuth();

  const features = [
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Global Community',
      description: 'Connect with talented professionals from around the world'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Safe & Secure',
      description: 'Built-in protection with escrow payments and governance'
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Fast Payments',
      description: 'Quick and secure payment processing for all transactions'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Quality Assurance',
      description: 'Ratings and reviews ensure high-quality work delivery'
    }
  ];

  const jobCategories = [
    { name: 'Technology', jobs: 1247, color: 'bg-blue-500' },
    { name: 'Design', jobs: 892, color: 'bg-purple-500' },
    { name: 'Writing', jobs: 634, color: 'bg-green-500' },
    { name: 'Marketing', jobs: 423, color: 'bg-orange-500' },
    { name: 'Engineering', jobs: 567, color: 'bg-red-500' },
    { name: 'Consulting', jobs: 398, color: 'bg-indigo-500' },
  ];

  const stats = [
    { number: '500K+', label: 'Active Talents' },
    { number: '50K+', label: 'Successful Projects' },
    { number: '$2M+', label: 'Paid to Talents' },
    { number: '99%', label: 'Client Satisfaction' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-accent/10 via-white to-blue-50 dark:from-accent/20 dark:via-gray-900 dark:to-gray-800 min-h-screen flex items-center relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading font-bold text-heading mb-8 leading-tight">
              Find Your Next{' '}
              <span className="text-gradient bg-gradient-to-r from-accent to-blue-600 bg-clip-text text-transparent">
                Freelance Opportunity
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-400 mb-12 max-w-4xl mx-auto leading-relaxed">
              Connect with clients and talents worldwide. Build your professional network, 
              grow your business, and achieve your career goals with Workloob.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-6 sm:space-y-0 sm:space-x-6">
              {isAuthenticated ? (
                <>
                  {user?.role === 'talent' ? (
                    <Link to="/jobs" className="btn-primary text-xl px-12 py-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                      Browse Jobs <ArrowRight className="w-6 h-6 ml-3" />
                    </Link>
                  ) : (
                    <Link to="/gigs" className="btn-primary text-xl px-12 py-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                      Browse Gigs <ArrowRight className="w-6 h-6 ml-3" />
                    </Link>
                  )}
                  <Link to="/dashboard" className="btn-outline text-xl px-12 py-4 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300">
                    Go to Dashboard
                  </Link>
                </>
              ) : (
                <AuthModalWidget />
              )}
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-bold text-accent">{stat.number}</div>
                  <div className="text-gray-600 dark:text-gray-400">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Popular Categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-heading font-bold text-heading mb-4">
              Popular Job Categories
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Explore thousands of opportunities across diverse industries
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {jobCategories.map((category, index) => (
              <Link
                key={index}
                to="/jobs"
                className="group p-6 bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 text-center border border-gray-200 dark:border-gray-700"
              >
                <div className={`w-12 h-12 ${category.color} rounded-lg mx-auto mb-3 flex items-center justify-center text-white`}>
                  <Globe className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  {category.name}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {category.jobs} jobs available
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-heading font-bold text-heading mb-4">
              Why Choose Workloob?
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              We provide everything you need to succeed in the freelance marketplace
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="text-accent mb-4 flex justify-center">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-heading mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-heading font-bold text-heading mb-4">
              How It Works
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Get started in minutes and begin your freelance journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* For Talents */}
            <div className="text-center">
              <div className="bg-accent/10 w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-accent">1</span>
              </div>
              <h3 className="text-xl font-semibold text-heading mb-2">Create Your Profile</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Sign up and showcase your skills, experience, and portfolio to attract clients
              </p>
            </div>

            <div className="text-center">
              <div className="bg-accent/10 w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-accent">2</span>
              </div>
              <h3 className="text-xl font-semibold text-heading mb-2">Find Opportunities</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Browse jobs that match your skills and apply with your best proposal
              </p>
            </div>

            <div className="text-center">
              <div className="bg-accent/10 w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-accent">3</span>
              </div>
              <h3 className="text-xl font-semibold text-heading mb-2">Collaborate & Get Paid</h3>
              <p className="text-gray-600 dark:text-gray-400">
                Work with clients, deliver quality work, and get paid securely through our platform
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-accent to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-heading font-bold mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl opacity-90 mb-8">
            Join thousands of professionals who are already building their careers on Workloob
          </p>
          {!isAuthenticated && <AuthModalWidget />}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
